import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiGet, apiPost } from "../lib/api";
import type { OutreachCampaign, OutreachRecipient } from "../lib/types";
import { useScope } from "../scope/ScopeContext";

/**
 * Ups — showroom visits that didn't buy.
 *
 * Its own page rather than a filter on the Manager feedback inbox, which is
 * the question this screen exists to answer. An up's response is stamped
 * `routed_to = 'private_feedback'` (there is no review destination for this
 * audience, and writing 'review' would be a lie in the column the dashboard
 * reads) — but "Manager feedback" means one specific thing to a store: an
 * unhappy customer, call them back. Somebody who walked because the trade
 * number was light is a sales lead, not a service recovery. Mixing them makes
 * neither list workable, so services/manager_queue.ts excludes unsold_visit
 * from that inbox and they live here instead. Kat, 2026-09-11.
 *
 * The comparison this page is for is the two-reason pair: what the CUSTOMER
 * said against what the SALESPERSON logged at the time. 68% of the logged end
 * reasons are "Other", which is the entire argument for asking the customer.
 */

const MODE_COPY: Record<string, { label: string; tone: string }> = {
  off: { label: "Off", tone: "bg-slate-100 text-slate-600" },
  approval: { label: "Needs approval", tone: "bg-amber-100 text-amber-800" },
  auto: { label: "Sending", tone: "bg-emerald-100 text-emerald-800" },
};

function yesNo(v: boolean | null | undefined): string {
  if (v === null || v === undefined) return "—";
  return v ? "Yes" : "No";
}

/**
 * "Did we treat you well?" read back out of the stored score, against the
 * campaign's OWN threshold rather than a hard-coded number — same rule as
 * OutreachRecipientDetailPage's greatJobLabel, so a campaign tuned differently
 * isn't mislabelled here.
 */
function treatedWell(r: OutreachRecipient): boolean | null {
  if (r.satisfactionScore == null) return null;
  return r.satisfactionScore >= (r.campaignSatisfactionThreshold ?? 4);
}

interface FollowupCounts {
  unhandled: number;
  handled: number;
  silent: number;
}

interface UpsResponse {
  recipients: OutreachRecipient[];
  total?: number;
  followupCounts?: FollowupCounts;
}

export function UpsPage() {
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState<FollowupCounts | null>(null);
  const { focusedDealerId } = useScope();
  const [campaigns, setCampaigns] = useState<OutreachCampaign[]>([]);
  const [recipients, setRecipients] = useState<OutreachRecipient[]>([]);
  const [showHandled, setShowHandled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        type: "unsold_visit",
        responded: "true",
        limit: "200",
      });
      if (focusedDealerId) params.set("dealerId", focusedDealerId);
      const [recipientRes, campaignRes] = await Promise.all([
        apiGet<UpsResponse>(`/api/outreach/recipients?${params.toString()}`),
        apiGet<{ campaigns: OutreachCampaign[] }>("/api/outreach/campaigns"),
      ]);
      setRecipients(
        recipientRes.recipients.sort((a, b) =>
          (b.respondedAt ?? "").localeCompare(a.respondedAt ?? "")
        )
      );
      setTotal(recipientRes.total ?? recipientRes.recipients.length);
      setCounts(recipientRes.followupCounts ?? null);
      setCampaigns(campaignRes.campaigns.filter((c) => c.type === "unsold_visit"));
    } catch (err) {
      console.error("ups load failed:", err);
    } finally {
      setLoading(false);
    }
  }, [focusedDealerId]);

  useEffect(() => {
    void load();
  }, [load]);

  const markHandled = async (id: string) => {
    setBusyId(id);
    try {
      await apiPost(`/api/outreach/recipients/${id}/acknowledge-feedback`);
      await load();
    } catch (err) {
      alert(`Couldn't mark handled: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setBusyId(null);
    }
  };

  const visible = recipients.filter((r) => showHandled || !r.privateFeedbackAcknowledgedAt);
  // From the server, over every up in scope — not over the rows on this page.
  const openCount =
    counts?.unhandled ?? recipients.filter((r) => !r.privateFeedbackAcknowledgedAt).length;
  const sending = campaigns.filter((c) => c.mode !== "off");

  return (
    <div className="mx-auto max-w-4xl px-4 py-6 sm:px-8 sm:py-8">
      <div className="flex flex-wrap items-start justify-between gap-3 sm:gap-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Ups</h1>
          <p className="mt-1 text-sm text-slate-600">
            People who visited the showroom and didn't buy — and what they say the reason was.
            No review is ever asked of this audience.
          </p>
        </div>
        {recipients.length > 0 && (
          <label className="flex shrink-0 items-center gap-2 pt-1 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={showHandled}
              onChange={(e) => setShowHandled(e.target.checked)}
            />
            Show handled
          </label>
        )}
      </div>

      {/* What the campaigns are actually doing. Leading with this because for
          now the honest answer is "nothing", and a page that looked broken
          instead of off would send someone hunting for a bug. */}
      {!loading && (
        <div className="mt-6 rounded-lg border border-slate-200 bg-white p-4">
          {campaigns.length === 0 ? (
            <p className="text-sm text-slate-600">No up campaigns yet. Set one up under Outreach settings.</p>
          ) : sending.length === 0 ? (
            <p className="text-sm text-slate-700">
              <span className="font-medium">Nothing is sending.</span>{" "}
              {campaigns.length} rooftop{campaigns.length === 1 ? "" : "s"} wired and switched off.
              {/* Deliberately not a link to Outreach settings: that screen
                  renders only the sales and service campaigns, so it cannot
                  turn one of these on. Saying so beats sending someone to a
                  page where the switch isn't. */}{" "}
              Turning one on is a deliberate step and isn't wired into this
              screen yet — ask before the first one goes live.
            </p>
          ) : (
            <p className="text-sm text-slate-700">
              <span className="font-medium">
                {sending.length} of {campaigns.length}
              </span>{" "}
              rooftop{sending.length === 1 ? " is" : "s are"} live.{" "}
              {openCount > 0 ? `${openCount} response${openCount === 1 ? "" : "s"} to work.` : ""}
            </p>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            {campaigns.map((c) => {
              const copy = MODE_COPY[c.mode] ?? { label: c.mode, tone: "bg-slate-100 text-slate-600" };
              return (
                <span
                  key={c.id}
                  className={`rounded-full px-2 py-0.5 text-[11px] ${copy.tone}`}
                  title={`${c.name} — ${copy.label}`}
                >
                  {c.name} · {copy.label}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {loading ? (
        <div className="mt-8 text-sm text-slate-500">Loading…</div>
      ) : visible.length === 0 ? (
        <div className="mt-8 rounded-lg border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">
          {recipients.length === 0
            ? "No one has answered yet. Responses land here as they come in."
            : "Everything here has been handled."}
        </div>
      ) : (
        <ul className="mt-6 space-y-3">
          {/* The list is capped; the counts above are not. Say so, or the page
              reads as the whole inbox. */}
            {!loading && total > recipients.length && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-xs text-amber-800">
              Showing the {recipients.length} most recent of <strong>{total}</strong>. Focus a
              single store to see the rest.
            </div>
          )}
          {visible.map((r) => {
            const name = [r.firstName, r.lastName].filter(Boolean).join(" ") || "A customer";
            const handled = !!r.privateFeedbackAcknowledgedAt;
            return (
              <li
                key={r.id}
                className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <Link
                      className="font-medium text-slate-900 hover:underline"
                      to={`/outreach/recipients/${r.id}`}
                    >
                      {name}
                    </Link>
                    <div className="text-xs text-slate-500">
                      {r.dealerName}
                      {r.vehicle ? ` · ${r.vehicle}` : ""}
                      {r.eventDate
                        ? ` · visited ${new Date(r.eventDate).toLocaleDateString("en-US")}`
                        : ""}
                    </div>
                  </div>
                  {handled ? (
                    <span className="shrink-0 text-xs text-slate-400">Handled</span>
                  ) : (
                    <button
                      className="shrink-0 rounded-md border border-slate-300 px-3 py-1 text-xs text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                      disabled={busyId === r.id}
                      onClick={() => void markHandled(r.id)}
                    >
                      {busyId === r.id ? "Saving…" : "Mark handled"}
                    </button>
                  )}
                </div>

                {/* The pair. Side by side on purpose — it is the whole point of
                    asking, and 68% of the logged reasons say only "Other". */}
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  <div className="rounded-md bg-slate-50 p-3">
                    <div className="text-[11px] uppercase tracking-wide text-slate-500">
                      They said
                    </div>
                    <div className="mt-1 text-sm text-slate-900">
                      {r.unsoldReason || "No reason given"}
                    </div>
                  </div>
                  <div className="rounded-md bg-slate-50 p-3">
                    <div className="text-[11px] uppercase tracking-wide text-slate-500">
                      Your team logged
                    </div>
                    <div className="mt-1 text-sm text-slate-900">{r.visitEndReason || "Other"}</div>
                  </div>
                </div>

                {r.unsoldFollowupAnswer && (
                  <blockquote className="mt-3 border-l-[3px] border-brand-500 pl-3 text-sm text-slate-700">
                    {r.unsoldFollowupAnswer}
                  </blockquote>
                )}

                <div className="mt-3 text-xs text-slate-500">
                  Treated well: {yesNo(treatedWell(r))} · Test drive: {yesNo(r.hadTestDrive)} ·
                  Written up: {yesNo(r.hadWriteUp)}
                  {r.phone ? ` · ${r.phone}` : ""}
                  {r.email ? ` · ${r.email}` : ""}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
